import { cn } from "@pma/utils/helpers";
import { Card } from "../common/card";
import { HTMLAttributes, useEffect, useRef, useState } from "react";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../common/form";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Input } from "../common/input";
import useTranslator from "@pma/hooks/use-translator";
import { PasswordInput } from "../common/password-input";
import { Button } from "../common/button";
import { LOGIN_COMPLETE_PAGE_ROUTE, LOGIN_PAGE_ROUTE } from "@pma/utils/page-routes";
import { Link, useNavigate } from "react-router-dom";
import Recaptcha from "./recaptcha";
import { DictType } from "@pma/types";
import { useMutation } from "@tanstack/react-query";
import { toast } from "@pma/hooks/use-toast";
import axios from "axios";
import { SERVER_URL, SIGNUP_ENDPOINT } from "@pma/utils/api-routes";
import { setCookie } from "nookies";
import { ACCESS_TOKEN, REGEX_PHONE_NUMBER } from "@pma/utils/constants";

interface SignupFormProps extends HTMLAttributes<HTMLDivElement> { }

const formSchema = z.object({
  name: z
    .string()
    .trim()
    .min(2, { message: "Please enter your name" })
    .max(50, { message: "Name should not contain more than 50 characters" }),
  email: z
    .string()
    .min(1, { message: "Please enter your email" })
    .email({ message: "Invalid email address" }),
  password: z
    .string()
    .min(1, {
      message: "Please enter your password",
    })
    .min(7, {
      message: "Password must be at least 7 characters long",
    }),
  confirm_password: z.string().min(1, {
    message: "Please confirm your password",
  }),
  phone_number: z
    .string()
    .min(1, { message: "Please enter your phone number" })
    .regex(REGEX_PHONE_NUMBER, "Invalid Number!"),
  organization_name: z
    .string()
    .trim()
    .max(255, { message: "Organization name should not contain more than 255 characters" })
    .optional(),
}).refine((data) => data.password === data.confirm_password, {
  message: "Passwords don't match.",
  path: ["confirm_password"],
});

const SignupForm = ({ className, ...props }: SignupFormProps) => {
  const { translate } = useTranslator();
  const navigate = useNavigate();
  const elementRef = useRef<HTMLElement | null>(null);

  const [recaptchaToken, setRecaptchaToken] = useState<DictType>({
    v2: "",
    v3: "",
  });

  const [submittedData, setSubmittedData] = useState({});
  const [v2Fallback, setV2Fallback] = useState(false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      password: "",
      confirm_password: "",
      phone_number: "",
      organization_name: "",
    },
  });

  const signup = async (payload: DictType) => {
    if (!v2Fallback && !recaptchaToken.v3) {
      return toast({
        title: translate("Info"),
        description: translate("Please complete the reCAPTCHA"),
        variant: "info",
      });
    }

    if ((v2Fallback && !payload?.v2Token) || !recaptchaToken.v3) {
      return;
    }

    const response = await axios.post(SERVER_URL + SIGNUP_ENDPOINT, {
      ...payload,
      recaptcha_response: !v2Fallback ? recaptchaToken.v3 : undefined,
      recaptcha_response_v2: payload?.v2Token,
    });

    return response.data;
  };

  const signupMutation = useMutation({
    mutationKey: ["signup", recaptchaToken],
    mutationFn: (payload: DictType) => signup(payload),
    onSuccess: (data) => {
      // persist in cookieStorage for  24hrs
      setCookie(null, ACCESS_TOKEN, data.authToken, {
        maxAge: 24 * 60 * 60,
        sameSite: "strict",
        path: "/",
      });

      toast({
        title: translate("Success"),
        description: translate("Singup successful"),
        variant: "success",
      });

      navigate(LOGIN_COMPLETE_PAGE_ROUTE);
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message;

      if (errorMessage === "Invalid Captcha Response. Please try again.") {
        setV2Fallback(true);
      } else {
        toast({
          title: translate("Error"),
          description: errorMessage || translate("Unable to signup at this time"),
          variant: "error",
        });
      }
    },
  });

  const onSubmit = (data: z.infer<typeof formSchema>) => {
    // Track for v2 resubmission
    setSubmittedData(data);

    signupMutation.mutate(data);
  }

  const reSubmit = (token: string) => {
    signupMutation.mutate({ ...submittedData, v2Token: token });
  };

  useEffect(() => {
    elementRef.current = document.getElementById('rog-bg-container-wrap');
    elementRef.current && elementRef.current.classList.add('signup-adjustment')
    return () => {
      elementRef.current && elementRef.current.classList.remove('signup-adjustment');
    }
  }, []);

  useEffect(() => {
    if (form.formState.isSubmitted) {
      if (form.formState.isValid) {
        elementRef.current && elementRef.current.classList.remove('signup-parent-hight-adjustment');
      } else {
        elementRef.current && elementRef.current.classList.add('signup-parent-hight-adjustment');
      }
    }
  }, [form.formState.isValid, form.formState.isSubmitted])

  return (
    <Card
      className="h-[500px] w-full rounded-md border-none p-6 md:w-[350px]"
      style={{
        background: "linear-gradient(blue 5.01%, rgb(232, 203, 203, 0%)",
        borderRadius: "15px 15px 0px 0px",
      }}
    >
      <div className="flex flex-col items-center justify-center space-y-2 text-left">
        <div className="relative mt-0 p-0">
          <img
            src={`${process.env.PUBLIC_URL}/group-232.svg`}
            alt="Brand logo"
          />
        </div>
      </div>

      <div className={cn("grid gap-6", className)} {...props}>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="text-white">
            <div className="flex flex-col gap-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem className="flex flex-col items-start justify-start space-y-2">
                    <FormLabel className="capitalize">
                      {translate("Name")}
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder={translate("Enter name")}
                        className="rounded-full text-black"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem className="flex flex-col items-start justify-start space-y-2">
                    <FormLabel className="capitalize">
                      {translate("Email")}
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder={"name@example.com"}
                        className="rounded-full text-black"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem className="flex w-full flex-col space-y-2">
                    <FormLabel className="w-full capitalize ltr:text-left rtl:text-right">
                      {translate("Password")}
                    </FormLabel>

                    <FormControl className="">
                      <PasswordInput
                        placeholder="********"
                        {...field}
                        className="w-full rounded-full bg-white text-black"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="confirm_password"
                render={({ field }) => (
                  <FormItem className="flex w-full flex-col space-y-2">
                    <FormLabel className="w-full capitalize ltr:text-left rtl:text-right">
                      {translate("Confirm Password")}
                    </FormLabel>

                    <FormControl className="">
                      <PasswordInput
                        placeholder="********"
                        {...field}
                        className="w-full rounded-full bg-white text-black"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="phone_number"
                render={({ field }) => (
                  <FormItem className="flex flex-col items-start justify-start space-y-2">
                    <FormLabel className="capitalize">
                      {translate("Phone Number")}
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder={translate("Enter phone number")}
                        className="rounded-full text-black"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="organization_name"
                render={({ field }) => (
                  <FormItem className="flex flex-col items-start justify-start space-y-2">
                    <FormLabel className="capitalize">
                      {translate("Organization")}
                    </FormLabel>
                    <FormControl>
                      <Input
                        placeholder={translate("Enter Organization")}
                        className="rounded-full text-black"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <Button
                className="mt-2 rounded-full bg-primary/80 capitalize"
                loading={signupMutation.isPending}
                disabled={signupMutation.isPending || signupMutation.isSuccess}
              >
                {translate("Sign up")}
              </Button>
            </div>
          </form>
        </Form>
      </div>
      <div className="my-3 flex items-center justify-end text-white/60">
        <Link
          to={LOGIN_PAGE_ROUTE}
          className="text-sm font-medium text-white/60 hover:opacity-75"
        >
          {translate("Log in")}?
        </Link>
      </div>

      {/* GOOGLE Recaptcha */}
      <Recaptcha
        setToken={setRecaptchaToken}
        v2={v2Fallback}
        reSubmit={reSubmit}
      />
    </Card>
  );
}

export default SignupForm